import { memo, useEffect, useRef, useState, type ElementType } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface StatCardProps {
  title: string;
  value: string | number;
  icon: ElementType;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'orange' | 'teal';
  isLoading?: boolean;
  subtitle?: string;
  onClick?: () => void;
  className?: string;
}

const colorClasses: Record<StatCardProps['color'], { bg: string; icon: string; accent: string }> = {
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    icon: 'text-blue-600 dark:text-blue-400',
    accent: 'border-blue-200 dark:border-blue-800',
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    icon: 'text-green-600 dark:text-green-400',
    accent: 'border-green-200 dark:border-green-800',
  },
  yellow: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    icon: 'text-yellow-600 dark:text-yellow-400',
    accent: 'border-yellow-200 dark:border-yellow-800',
  },
  red: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    icon: 'text-red-600 dark:text-red-400',
    accent: 'border-red-200 dark:border-red-800',
  },
  purple: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    icon: 'text-purple-600 dark:text-purple-400',
    accent: 'border-purple-200 dark:border-purple-800',
  },
  orange: {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    icon: 'text-orange-600 dark:text-orange-400',
    accent: 'border-orange-200 dark:border-orange-800',
  },
  teal: {
    bg: 'bg-teal-50 dark:bg-teal-900/20',
    icon: 'text-teal-600 dark:text-teal-400',
    accent: 'border-teal-200 dark:border-teal-800',
  },
};

function StatCardComponent({
  title,
  value,
  icon: Icon,
  trend,
  color,
  isLoading = false,
  subtitle,
  onClick,
  className,
}: StatCardProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const prevValueRef = useRef(value);
  const colors = colorClasses[color];

  // Animate on value change
  useEffect(() => {
    if (prevValueRef.current !== value && !isLoading) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 600);
      prevValueRef.current = value;
      return () => clearTimeout(timer);
    }
  }, [value, isLoading]);

  const CardContent = (
    <>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
            {title}
          </p>
          {isLoading ? (
            <div className="mt-2 h-8 w-24 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse" />
          ) : (
            <p
              className={cn(
                'mt-2 text-3xl font-bold text-secondary-900 dark:text-secondary-100 transition-all duration-300',
                isAnimating && 'scale-110 text-primary-600 dark:text-primary-400'
              )}
            >
              {value}
            </p>
          )}
          {subtitle && !isLoading && (
            <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
              {subtitle}
            </p>
          )}
          {trend && !isLoading && (
            <div className="mt-2 flex items-center text-sm">
              {trend.isPositive ? (
                <TrendingUp
                  className="h-4 w-4 text-green-500 ltr:mr-1 rtl:ml-1"
                  aria-hidden="true"
                />
              ) : (
                <TrendingDown
                  className="h-4 w-4 text-red-500 ltr:mr-1 rtl:ml-1"
                  aria-hidden="true"
                />
              )}
              <span
                className={cn(
                  'font-medium',
                  trend.isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                )}
              >
                {trend.value > 0 ? '+' : ''}{trend.value}%
              </span>
              <span className="text-secondary-500 dark:text-secondary-400 ltr:ml-1 rtl:mr-1">
                vs last period
              </span>
            </div>
          )}
        </div>
        <div
          className={cn(
            'rounded-lg p-3 shrink-0 ltr:ml-4 rtl:mr-4',
            colors.bg
          )}
          aria-hidden="true"
        >
          {isLoading ? (
            <div className="h-6 w-6 bg-secondary-300 dark:bg-secondary-600 rounded animate-pulse" />
          ) : (
            <Icon className={cn('h-6 w-6', colors.icon)} />
          )}
        </div>
      </div>
    </>
  );

  const baseClasses = cn(
    'bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6 shadow-sm',
    'transition-all duration-200',
    onClick && 'cursor-pointer hover:shadow-md hover:border-secondary-300 dark:hover:border-secondary-600',
    className
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={cn(baseClasses, 'text-left w-full')}
        onClick={onClick}
        aria-label={`${title}: ${value}`}
      >
        {CardContent}
      </button>
    );
  }

  return (
    <div className={baseClasses} role="region" aria-label={`${title}: ${value}`}>
      {CardContent}
    </div>
  );
}

export const StatCard = memo(StatCardComponent);
export default StatCard;
